import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CalendarClock, CheckCheck, Gift, Handshake, ShieldCheck, UserPlus, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const ICONS = { repair: [Wrench, "tone-peach"], claim: [ShieldCheck, "tone-lilac"], alert: [CalendarClock, "tone-butter"], lead: [Handshake, "tone-mint"], friend: [UserPlus, "tone-sky"], pot: [Gift, "tone-lilac"], birthday: [Gift, "tone-rose"] };
const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  if (m < 1440) return `il y a ${Math.round(m / 60)} h`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

export const NotificationsBell = ({ className }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data), refetchInterval: 20000, refetchOnWindowFocus: true });
  const seen = useRef(null);
  useEffect(() => {
    if (!data) { return; }
    const ids = new Set(data.items.map((n) => n.id));
    if (seen.current) {
      data.items.filter((n) => !n.read && !seen.current.has(n.id)).slice(0, 3).forEach((n) => toast(n.title, { description: n.body?.slice(0, 120), action: { label: "Voir", onClick: () => open(n) } }));
    }
    seen.current = ids;
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const open = async (n) => {
    setIsOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    refresh();
    navigate(n.link || "/");
  };
  const unread = data?.unread || 0;
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger className={cn(className, "relative")} aria-label="Notifications" data-testid="notifications-button">
        <Bell className="h-4 w-4" />
        {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground" data-testid="notifications-unread">{unread > 9 ? "9+" : unread}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" collisionPadding={12} className="w-[min(22rem,calc(100vw-1.5rem))] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && <button className="flex items-center gap-1 text-xs text-primary" onClick={async () => { await api.post("/notifications/read-all"); refresh(); }} data-testid="notifications-read-all"><CheckCheck className="h-3.5 w-3.5" />Tout marquer comme lu</button>}
        </div>
        <ul className="max-h-[60vh] overflow-y-auto">
          {(data?.items || []).length === 0 && <li className="p-6 text-center text-sm text-muted-foreground">Aucune notification pour l'instant.</li>}
          {(data?.items || []).map((n) => {
            const [Icon, tone] = ICONS[n.kind] || ICONS.alert;
            return (
              <li key={n.id}>
                <button onClick={() => open(n)} className={cn("flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary", !n.read && "bg-primary/5")} data-testid={`notification-${n.id}`}>
                  <span className={cn("tone-chip flex h-7 w-7 shrink-0 items-center justify-center rounded-lg", tone)}><Icon className="h-3.5 w-3.5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start gap-2"><span className="flex-1 text-sm font-semibold leading-snug">{n.title}</span>{!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}</span>
                    <span className="line-clamp-2 whitespace-pre-line text-xs text-muted-foreground">{n.body}</span>
                    <span className="text-[11px] text-muted-foreground">{ago(n.created_at)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
